import Image from 'next/image';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { ComponentProps } from '@components/strapi/DynamicZoneComponent';
import { TeamSectionDTO } from '@lib/strapi/dtos/components/sections/teamSection.dto';
import { SectionHeading, dataOf, blobFor } from '../shared-ui';
import { IconPeople } from '@components/utils/icons';

/**
 * The clinicians. Headshots take the organic mask; a member without a photo
 * gets the brand symbol on a tint with a visible pending badge — never a
 * stand-in face (D49): an invented portrait under a named clinician's name is
 * the one shortcut not worth taking. The studio shots swap in as a CMS edit.
 *
 * Every card links to the member's CV page at root-level /[slug] (the D35
 * union). On a member's OWN page — this section is the "rest of the team"
 * band there — the member being viewed is filtered out: a card that links to
 * the page you are on is noise.
 */
const TeamSectionImplementation = ({ section, page }: ComponentProps<TeamSectionDTO>) => {
  const { t } = useTranslation('main');
  if (!section) return null;
  const currentSlug = (page as unknown as { entity?: { slug?: string }; entityKind?: string } | undefined);
  const selfSlug = currentSlug?.entityKind === 'team' ? currentSlug?.entity?.slug : undefined;
  const items = ((dataOf(page).team ?? []) as {
    slug?: string; displayName?: string; qualification?: string; role?: string;
    bioTeaser?: string; photo?: { url: string; alternativeText?: string };
  }[]).filter((m) => !selfSlug || m.slug !== selfSlug);
  if (!items.length) return null;

  // With no section heading there is no h2 between the page h1 and the card
  // titles — heading-order violation (Lighthouse a11y, 2026-09-16; same rule
  // as StepsSection). Card titles take the level that follows what exists.
  const CardTitle = section.heading ? 'h3' : 'h2';
  return (
    <section className="my-14 md:my-20">
      <SectionHeading heading={section.heading} intro={section.intro} icon={<IconPeople />} />
      <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((m, i) => (
          <Link key={m.slug ?? i} href={`/${m.slug}`} className="group block">
            <div className={`${blobFor(i)} relative aspect-square w-full max-w-[16rem] overflow-hidden transition-transform group-hover:-translate-y-1 ${m.photo ? 'bg-violet-400' : 'bg-violet-050'}`}>
              {m.photo ? (
                <Image src={m.photo.url} alt={m.displayName ?? ''} fill
                  // (min-resolution:2dppx) pins retina to the 512w variant = EXACTLY 2x of
                  // the 256px card — the plain 16rem hint made high-DPR browsers round UP
                  // to 640/750w (2.5-2.9x), which Lighthouse flags as oversized (its math
                  // caps at 2x — and 2x is also the owner's stated retina target)
                  sizes="(min-resolution: 2dppx) 12.1rem, 16rem" quality={90} className="portrait-photo" />
              ) : (
                // the brand symbol alone until the shoot lands (D49) — the
                // «αναμονή φωτογράφισης» badge dropped (owner, 2026-09-10):
                // an internal to-do has no business on the public card
                <div className="absolute inset-0 grid place-items-center">
                  <Image src="/images/brand/symbol-color.svg" alt="" width={72} height={102} />
                </div>
              )}
            </div>
            <CardTitle className="mt-4 text-lg font-extrabold text-ink group-hover:text-violet-700">{m.displayName}</CardTitle>
            {/* credentials are TRUST content («Pt., BSc.» is what a patient
                checks) — 15px, not the 14px metadata floor (owner, 2026-09-10) */}
            {(m.qualification || m.role) ? (
              <p className="mt-1 text-[0.9375rem] text-muted">
                {[m.qualification, m.role].filter(Boolean).join(' · ')}
              </p>
            ) : null}
            {/* teaser only (owner, 2026-09-08) — the full narrative overwhelmed
                the card grid; it lives on the member's own page, one click away */}
            {section.showBio && m.bioTeaser ? (
              <>
                <p className="mt-3 line-clamp-4 text-[0.9375rem] leading-relaxed text-body">{m.bioTeaser}</p>
                <span className="mt-2 inline-block text-sm font-extrabold text-violet-700 group-hover:underline">
                  {t('team.profileCta')}
                </span>
              </>
            ) : null}
          </Link>
        ))}
      </div>
    </section>
  );
};

export default TeamSectionImplementation;
